import PageTreeViewModel from "../modelTreeViews/PageTreeviewModel";
import { EventKey } from "../eventLogger/EventKeys";

import BaseModel from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import Consumer from "./Consumer";
import Point from "./Point";
import Room from "./Room";
import { copyProperties, formatDate, later } from "./utils";
import Connection from "./Connection";
import type ConnectableObject from "./ConnectableObject";
import { ChannelType, channelTypeFromString, DeviceType, isPlugChannelType } from "./ChannelType";
import Button from "./Button";
import BaseObject from "./BaseObject";
import ObjectFactory from "./ObjectFactory";
import { dataUrlToCanvas, dataUrlToImage, pointInBounderyRect, pointInRect } from "../modelViews/GraficElementUtils";
import Logger from "../Logger";
import PageGroup, { fixedPageGroupTags } from "./PageGroup";
import type DeviceTemplate from "./DeviceTemplate";
import PdfFile from "./PdfFile";
import Memo from "./Memo";
import { itx } from "../translationItx/itx";

import Size from "./Size";
import type Floor from "./Floor";
import { showErrToast } from "../components/Toasts";
import { showGlobalWaitIndicator } from "../components/GlobalWaitIndicator";
import WatchableValue from "./WatchableValue";
import BaseModelReference from "./BaseModelReference";
import BackgroundImagePosition from "./BackgroundImagePosition";
import BoundaryRect from "./BoundaryRect";
import RoomToFloorTransform from "./RoomToFloorTransform";
import { openRoomDialog } from "../modelDialogs/RoomDialog";
import { PageTypes, PagePrintBorders, pageSizeA4Landscape } from "./PageTypes";
import Cabinet from "./Cabinet";
import { DeviceClasses } from "./DeviceClasses";

const logger = new Logger();

export const mmToPixel = 3.779527559;

ObjectFactory.registerClass("Page", (data?: any, parent?: BaseModel) => {
  return new Page(data, parent);
});

//////////////////////////////////////////////////
export default class Page extends BaseModel {
  public get className(): string {
    return "Page"; // no itx
  }

  // -----------------------------------------------------------------------------
  // targetObjectId
  // -----------------------------------------------------------------------------
  _targetObjectId: string | undefined;
  get targetObjectId(): string | undefined {
    return this._targetObjectId;
  }

  set targetObjectId(newValue: string | undefined) {
    if (this._targetObjectId === newValue) return;
    this.beforeChange("targetObjectId");
    this._targetObjectId = newValue;
    this.changed("targetObjectId");
  }

  // -----------------------------------------------------------------------------
  // documentType
  // -----------------------------------------------------------------------------
  _documentType = {
    type: PageTypes.roomplan,
    name: state.translate("pageDocumentType.roomplan", "Raumplan"),
  };
  get documentType(): { type: PageTypes; name: string } {
    return this._documentType;
  }

  set documentType(newValue: { type: PageTypes; name: string }) {
    if (this._documentType === newValue) return;
    this.beforeChange("documentType");
    this._documentType = newValue;
    this.changed("documentType");
  }

  // -----------------------------------------------------------------------------
  private documentCreatorNew: string | undefined;

  get documentTag(): string {
    if (this.parent) {
      return this.parent.fullTag;
    }
    return "";
  }

  get documentTitle(): string {
    if (this.parent) {
      return this.parent.name;
    }
    return "";
  }

  set documentTitle(newValue: string) {
    if (this.parent) {
      this.parent.name = newValue;
      this.parent.raiseOnChange("name");
    }
  }

  get documentDate(): string {
    return formatDate(new Date());
  }

  get documentCreator(): string {
    if (!this.documentCreatorNew) {
      this.documentCreatorNew = state.settings.userName;
    }
    return this.documentCreatorNew || state.settings.userName || itx("page.noName", "no name");
  }

  set isDirty(newValue: boolean) {
    if (this.documentCreatorNew != state.settings.userName && newValue) {
      this.documentCreatorNew = state.settings.userName;
    }
    super.isDirty = newValue;
  }

  // -----------------------------------------------------------------------------
  // layout
  // -----------------------------------------------------------------------------
  _layout = "landscape";
  get layout(): string {
    return this._layout;
  }

  set layout(newValue: string) {
    if (this._layout === newValue) return;
    this.beforeChange("layout");
    this._layout = newValue;
    this.changed("layout");
  }

  // ----------------------------------------------------------------------------- // no itx
  _backgroundImgUrl = "";

  // -----------------------------------------------------------------------------
  // date
  // -----------------------------------------------------------------------------
  _date = "";
  get date(): string {
    return this._date;
  }

  set date(newValue: string) {
    if (this._date === newValue) return;
    this.beforeChange("date");
    this._date = newValue;
    this.changed("date");
  }

  // -----------------------------------------------------------------------------
  // note
  // -----------------------------------------------------------------------------
  _note = "";
  get note(): string {
    return this._note;
  }

  set note(newValue: string) {
    if (this._note === newValue) return;
    this.beforeChange("note");
    this._note = newValue;
    this.changed("note");
  }

  // -----------------------------------------------------------------------------

  adjustActive = new WatchableValue<boolean>(false, "adjustActive"); // no itx

  // -----------------------------------------------------------------------------
  // editRooms
  // -----------------------------------------------------------------------------
  _editRooms?: boolean;

  get editRooms(): boolean {
    return this._editRooms == true;
  }

  set editRooms(newValue: boolean) {
    this._editRooms = newValue;
    this.raiseOnChange("editRooms");
  }

  // -----------------------------------------------------------------------------

  set backgroundImgUrl(newValue) {
    this.beforeChange("backgroundImgUrl");
    this._backgroundImgUrl = newValue;
    this._loadedImage = undefined;

    this.changed("backgroundImgUrl");
  }
  get backgroundImgUrl(): string {
    return this._backgroundImgUrl;
  }

  _loadedImage: HTMLImageElement | undefined;
  async loadBackgroundImage() {
    if (!this._loadedImage && this._backgroundImgUrl) {
      this._loadedImage = await dataUrlToImage(this._backgroundImgUrl);
    }
    return this._loadedImage;
  }

  //
  // all Sizes in cm
  //

  // -----------------------------------------------------------------------------
  // printBorders
  // -----------------------------------------------------------------------------
  _printBorders = { ...PagePrintBorders };
  get printBorders(): { Left: number; Right: number; Top: number; Bottom: number } {
    return this._printBorders;
  }

  set printBorders(newValue: { Left: number; Right: number; Top: number; Bottom: number }) {
    if (this._printBorders === newValue) return;
    this.beforeChange("printBorders");
    this._printBorders = newValue;
    this.changed("printBorders");
  }

  // -----------------------------------------------------------------------------
  // pagesize
  // -----------------------------------------------------------------------------
  _pagesize = { ...pageSizeA4Landscape };
  get pagesize(): { width: number; height: number } {
    return this._pagesize;
  }

  set pagesize(newValue: { width: number; height: number }) {
    if (this._pagesize === newValue) return;
    this.beforeChange("pagesize");
    this._pagesize = newValue;
    this.changed("pagesize");
  }

  // -----------------------------------------------------------------------------

  get sizeRect(): DOMRect {
    return new DOMRect(0, 0, this.pagesize.height, this.pagesize.height);
  }

  get sizeRectPx(): DOMRect {
    return new DOMRect(0, 0, this.pagesize.width * 10 * mmToPixel, this.pagesize.height * 10 * mmToPixel);
  }

  get sizePx(): Size {
    return new Size(this.pagesize.width * 10 * mmToPixel, this.pagesize.height * 10 * mmToPixel);
  }

  set sizePx(newValue: Size) {
    this.pagesize = { width: newValue.width / 10 / mmToPixel, height: newValue.height / 10 / mmToPixel };
  }

  // -----------------------------------------------------------------------------
  // backgroundImagePosition
  // -----------------------------------------------------------------------------
  _backgroundImagePosition = new BackgroundImagePosition();
  get backgroundImagePosition(): BackgroundImagePosition {
    return this._backgroundImagePosition;
  }

  set backgroundImagePosition(newValue: BackgroundImagePosition) {
    if (this._backgroundImagePosition === newValue) return;
    this.beforeChange("backgroundImagePosition");
    this._backgroundImagePosition = newValue;
    this.changed("backgroundImagePosition");
  }

  // -----------------------------------------------------------------------------

  connections = new BaseModelMap<Connection>(Connection, this, "con"); // no itx
  memos = new BaseModelMap<Memo>(Memo, this, "me"); // no itx

  constructor(data?: {}, parent?: BaseModel) {
    super(data, parent);
    //this.propertyId = "p";
    copyProperties(data, this);
    this.treeViewModel = new PageTreeViewModel(this);
    this.documentCreatorNew = this.documentCreator;

    console.log("Page constructor");
  }

  getPersistentProperties(): string[] {
    return [
      "connections", // no itx
      "size", // no itx
      "pagesize", // no itx
      "documentType", // no itx
      "printBorders", // no itx
      "layout", // no itx
      "_backgroundImgUrl", // no itx
      "date", // no itx
      "backgroundImagePosition", // no itx
      "targetObjectId", // no itx
      "note", // no itx
      "memos", // no itx
      "pdfPageRefId", // no itx
      "pageNumber", // no itx
      ...super.getPersistentProperties(),
    ];
  }

  async loadFromEstadaStorage(
    data: any,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> {
    const res = await super.loadFromEstadaStorage(data, parent, loadData);
    this.printBorders = PagePrintBorders;
    return res;
  }

  async saveToEstadaStorage(
    parentCollection: BaseModelMap<BaseModel> | undefined,
    saveItem: (item: BaseModel) => Promise<boolean>,
    parentObject?: BaseModel, // for object with a single reference (room.page)
  ): Promise<boolean> {
    try {
      if (this._pdfFile && this._pdfFile.needstoBeSaved) {
        await this._pdfFile.saveToDatabase(this.pdfFileId());
      }
    } catch (ex) {
      logger.error(`Error in Page.saveToEstadaStorage ${(ex as any).message}`, ex);
      return false;
    }

    return super.saveToEstadaStorage(parentCollection, saveItem, parentObject);
  }

  ///////// for now non pesistent .....
  numberOfSheets: number = 1;

  /// only for CoverPage !!!

  // -----------------------------------------------------------------------------
  // firstSheetNumber
  // -----------------------------------------------------------------------------
  _firstSheetNumber = -99;
  get firstSheetNumber(): number {
    return this._firstSheetNumber;
  }

  set firstSheetNumber(newValue: number) {
    if (newValue != this._firstSheetNumber) {
      this._firstSheetNumber = newValue;
      this.raiseOnChange("firstSheetNumber");

      state.coverPage?.raiseOnChange("firstSheetNumber");
    }
  }

  // -----------------------------------------------------------------------------

  // Nutzbare Zeichnungshöhe ohne Rand und Header

  get freedrawingSpaceHeight(): number {
    const headerHeight = 1.2;
    const frameSize = 1;
    const listPadding = 0.5;
    const extraHeight = 1;

    return this.pagesize.height - headerHeight - frameSize - listPadding - extraHeight;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////
  // delete elem
  getUsages(): Array<BaseModelReference> {
    const res = new Array<BaseModelReference>();

    state.floors.forEach((floor) => {
      if (floor.page?.pdfPageRefId == this.id || floor.c1?.pdfSource?.documentId === this.id) {
        res.push(new BaseModelReference({ referencedObject: floor }, this));
      }

      // if (floor.page?.pdfPageRef && this.pages.asArray().includes(floor.page.pdfPageRef)) {
      //   res.push(new BaseModelReference({ referencedObject: floor }, this));
      // }
      floor.rooms.forEach((room) => {
        if (room.page?.pdfPageRefId == this.id) {
          res.push(new BaseModelReference({ referencedObject: room }, this));
        }
      });
    });

    return res.sort((a, b) => a.fullTag.localeCompare(b.fullTag));
  }
  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const p = [this.getUsages().length];
      return state.translate("PageGroup.templateIsUsedT1Times", `Dieser Plan wird T1 mal verwendet`, {
        T1: `${p[0]}`,
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }

  canBeDeletedInteractive(): boolean {
    ////////////////////////////////////////////
    //buildingPlans
    ////////////////////////////////////////////
    if (this.parent?.tag == fixedPageGroupTags.buildingPlans && this.parent?.parent == state.pageGroups) {
      if (this.getUsages().length > 0) {
        return false;
      }
    }
    return true;
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    const parent = this.parent as any;
    this.getUsages().forEach((bmref) => {
      const page = (bmref.referencedObject as any)?.page as Page; // could be room or floor
      if (page) {
        page.removeBackgroundImage();
        page.raiseOnChange("backgroundImgUrl");
      }
    });

    if (parent.className == "Room") {
      (parent as Room).page = undefined;
    } else if (parent.className == "Floor") {
      (parent as Floor).page = undefined;
    }

    let res = this.connections.deleteAllItems();

    // delete this
    res = res.concat(super.delete());
    return res;
  }

  undelete() {
    super.undelete();

    const parent = this.parent as any;
    if (parent.className == "Room") {
      (parent as Room).page = this;
    } else if (parent.className == "Floor") {
      (parent as Floor).page = this;
    }
  }

  //
  getFullTagParts(): Array<any> {
    var res = new Array<any>();

    if (this.parent) {
      // use getFullTagParts from channal !!
      var res = this.parent.getFullTagParts();
    }

    res.push({ prefix: this.getTagPrefix(), tag: this.tag, obj: this });

    return res;
  }

  ////////////////////////////////////////////////////////////////////
  get targetObject(): BaseModel | undefined {
    return this.state.objectRegistry.getObject(this.targetObjectId);
  }

  set targetObject(newValue: BaseModel | undefined) {
    this.targetObjectId = newValue?.id;
  }

  ////////////////////////////////////////////////////////////////////

  get cabinetId(): string {
    if (this.parent) {
      return (this.parent as any).cabinetId;
    } else {
      return "";
    }
  }
  set cabinetId(newValue: string) {
    if (this.parent) {
      (this.parent as any).cabinetId = newValue;
    }
  }

  get frameWidth() {
    return 0.25;
  }

  get printableArea() {
    const border = this.printBorders;

    return {
      width: this.pagesize.width - border.Left - border.Right,
      height: this.pagesize.height - border.Top - border.Bottom,
    };
  }

  get printableAreaInPixel() {
    return {
      width: this.printableArea.width * 10 * mmToPixel,
      height: this.printableArea.height * 10 * mmToPixel,
    };
  }

  get contentAreaInPixel() {
    return {
      width: (this.printableArea.width - 2 * this.frameWidth) * 10 * mmToPixel,
      height: (this.printableArea.height - 2 * this.frameWidth) * 10 * mmToPixel,
    };
  }

  get isActive(): boolean {
    return state.activePaneId == this.id;
  }

  ////////////////////////////////////////////////////////////////////

  connectionManipulationActive = false;
  screen2 = false;
  currentZoom = new WatchableValue<number>(NaN, "currentZoom"); // no itx
  currentPan = new WatchableValue<Point>(new Point(0, 0), "currentPan"); // no itx
  fixedCabinetView = new WatchableValue<boolean>(false, "fixedCabinetView"); // no itx
  minZoom = 1;
  recalcConnections = new WatchableValue<number>(0, "recalcConnections"); // no itx

  ////////////////////////////////////////////////////////////////////

  getRoom(): Room | undefined {
    if (this.parent && this.parent.className == "Floor") {
      return (this.parent as Floor).getRoomGeneral();
    }
    if (this.parent && this.parent.className == "Room") {
      return this.parent as Room;
    } else {
      return undefined;
    }
  }
  get consumers(): BaseModelMap<Consumer> {
    const room = this.getRoom();
    if (room) {
      return room.consumers;
    } else {
      //
      // consumers for Floor drawing are dummy
      // Connections are drawn from inside der RoomViews
      //
      return new BaseModelMap<Consumer>(Consumer, undefined, undefined); // undefined: because we are not owner, only referencer
    }
  }

  removeConsumer(consumer: Consumer) {
    const room = this.getRoom();
    if (room) {
      return room.removeConsumer(consumer);
    } else {
      logger.error("addConsumer in floor drawing not implemented yet");
    }
  }

  addConsumer(consumer: Consumer) {
    const room = this.getRoom();
    if (room) {
      room.addConsumer(consumer);
    } else {
      logger.error("addConsumer in floor drawing not implemented yet");
    }
  }

  addMemo(memo: Memo) {
    memo.parent = this;
    this.memos.add(memo);
  }

  ////////////////////////////////////////////////////////////////////

  get buttons(): BaseModelMap<Button> {
    const room = this.getRoom();
    if (room) {
      return room.buttons;
    } else {
      //
      // consumers for Floor drawing are dummy
      // Connections are drawn from inside der RoomViews
      //
      return new BaseModelMap<Button>(Button, undefined, undefined);
    }
  }

  removeButton(button: Button) {
    const room = this.getRoom();
    if (room) {
      room.removeButton(button);
    } else {
      logger.error("removeButton in floor drawing not implemented yet");
    }
  }

  addButton(button: Button) {
    const room = this.getRoom();
    if (room) {
      room.addButton(button);
    } else {
      logger.error("addButton in floor drawing not implemented yet");
    }
  }

  allConnecableObjects(): Array<ConnectableObject> {
    const res: Array<ConnectableObject> = [...this.consumers.asArray(), ...state.allActors(), ...state.allScenes()];
    return res;
  }

  deleteConnection(connection: Connection) {
    this.connections.removeWithoutUpdate(connection);

    const elm = document.getElementById(connection.id);
    if (elm) {
      // MWE 5.2.22 just hide the element
      //
      // deleting the element results in an react error:
      // react trys to remove the element, already deleted by us here!!!!
      elm.style.display = "none";
    }
  }

  updateConnections() {
    logger.log("updateConnections");
    setTimeout(() => {
      this.connections.forEach((connection) => connection.updateEndPoints());

      if (this.isFloorPlan) {
        let floor = this.parent as Floor;
        floor.rooms.forEach((room) => {
          if (!room.isFloorGeneralRoom) {
            room.page?.updateConnections();
          }
        });
      }
    }, 100);
  }
  setAlignment(
    alignment: "horizontal" | "vertical", // no itx
  ) {
    if (alignment == "horizontal") {
      this.pagesize = {
        width: 29.7,
        height: 21,
      };
    } else if (alignment == "vertical") {
      this.pagesize = {
        width: 21,
        height: 29.7,
      };
    }
  }
  getAlignment():
    | "horizontal" // no itx
    | "vertical" {
    if (this.pagesize.width > this.pagesize.height) {
      return "horizontal"; // no itx
    } else {
      return "vertical"; // no itx
    }
  }

  cleanUpInavalidConnections() {
    //const connectionsToDelete = new Array<Connection>();
    this.connections.forEach((connection) => {
      if (connection.fromDevice == undefined || connection.toDevice == undefined) {
        connection.delete();
        // connectionsToDelete.push(connection);
      }
    });
  }

  async createNewConsumerInPage(
    room: Room,
    dataFromTool: any,
    p: Point,
    cabinet?: Cabinet,
    autoConnectChannel?: boolean,
  ): Promise<Consumer> {
    const channelType = channelTypeFromString(dataFromTool.type);

    // count the consumer from the same type
    const consumer = new Consumer(
      {
        channelType: channelType, //type
        roomId: room.id,
        // name: `${channelType}-${index}`,
        image: dataFromTool.image,
        name: dataFromTool.name,
      },
      room,
    );
    if (isPlugChannelType(consumer.channelType)) {
      consumer.prefixType = consumer.channelType;
    } else if (dataFromTool.tag == state.settings.consumerPrefix.switchOutlet) {
      consumer.channelType = consumer.prefixType = ChannelType.plug_Switch;
    } else if (dataFromTool.tag == state.settings.consumerPrefix.outlet) {
      consumer.channelType = consumer.prefixType = ChannelType.plug_230V;
    }
    if (dataFromTool.daliFlag) {
      consumer.isDali = true;
    }

    consumer.tag = state.nameAndTagManager.generateTag(consumer);

    var undoobjects = new Array<BaseModel>();
    undoobjects = undoobjects.concat(await this.addConsumerToPage(consumer, p, cabinet, autoConnectChannel));
    undoobjects.forEach((item) => {});

    return consumer;
  }
  async createNewRoomInPage(room: Room, dataFromTool: any, p: Point) {
    let floor = this.parent as Floor;
    if (floor.className == "Room") {
      floor = floor.parent as Floor;
    }
    if (floor.className == "Floor") {
      const newRoom = new Room({ tag: "@N", name: "" }, floor); // no itx
      floor.rooms.add(newRoom);
      await this.createRoomInPage(newRoom, p);
      if (this.parent) {
        openRoomDialog(newRoom, floor);
      }
    }
  }

  async createNewMemoInPage(room: Room, dataFromTool: any, p: Point): Promise<Memo> {
    const page = room.page;
    // count the consumer from the same type
    const memo = new Memo({}, page);

    var undoobjects = new Array<BaseModel>();
    undoobjects = undoobjects.concat(await this.addMemoToPage(memo, p));
    undoobjects.forEach((item) => {});

    return memo;
  }

  async addConsumerToPage(
    consumer: Consumer,
    p: Point | undefined,
    cabinet?: Cabinet,
    autoConnectChannel?: boolean,
  ): Promise<Array<BaseModel>> {
    if (p) {
      const center = new Point(0, 0.5);
      const placementP = new Point(p.x - center.x, p.y - center.y);

      consumer.position = placementP;
    }

    var res = new Array<BaseModel>();

    let autoConnect = autoConnectChannel == false ? false : true;
    res.push(consumer);

    this.addConsumer(consumer);
    var createdObjects = new Array<BaseModel>();
    if (consumer.channelType != ChannelType.Unknown && autoConnect) {
      createdObjects = await later(() => {
        return consumer.autoConnectChannel(this, cabinet, consumer.isDali);
      }, 100);
    }
    createdObjects.forEach((obj) => {
      res.push(obj);
    });

    state.lastCreatedObjectForGuide = consumer;

    return res;
  }

  async addMemoToPage(memo: Memo, p: Point | undefined): Promise<Array<BaseModel>> {
    if (p) {
      const center = new Point(0, 0.5);
      const placementP = new Point(p.x - center.x, p.y - center.y);
      memo.position = placementP;
    }

    var res = new Array<BaseModel>();
    res.push(memo);

    this.addMemo(memo);

    // res.concat(createdObjects);//don`t work why??
    state.lastCreatedObjectForGuide = memo;

    return res;
  }

  createNewButtonInPage(room: Room, dataFromTool: any, p: Point): Button | undefined {
    // numbering of the buttons
    if (dataFromTool.id) {
      const obj = this.state.objectRegistry.getObject(dataFromTool.id);
      if (obj) {
        if (obj.className == "DeviceTemplate") {
          if ((obj as DeviceTemplate).isActor) {
            // Actor is OK, decentral Actor (MWE 4.5.2025)
          } else {
            // Button is OK
          }
        }
      } else {
        return undefined;
      }
    }

    const button = new Button(
      {
        roomId: room.id,
      },
      room,
    );
    button.findFreePhysicalAddress();
    if (dataFromTool.image) {
      button.image = dataFromTool.image;
    }
    if (dataFromTool.classification?.name == "SensorNew") {
      button.classification = DeviceClasses.SensorNew;
      button.image = dataFromTool.image || state.hostBaseUrl + "images/consumer/Praesenzmelder.png";
    } else if (dataFromTool.classification?.name == "DecentralActor") {
      button.classification = DeviceClasses.DecentralActor;
      button.image = dataFromTool.image || state.hostBaseUrl + "images/buttons/touch-actor.png"; // no itx
    }
    button.tag = state.nameAndTagManager.generateTag(button); // has to be after setting the deviceType because of the different tag prefix for decentralactor and sensors

    this.addButtonToPage(button, p);

    if (dataFromTool.id) {
      button.templateId = dataFromTool.id;
      if (button.template) {
        button.name += " " + button.template.name;
      }
    }

    return button;
  }

  get selectedObjectInPage(): boolean {
    const selectedObject = state.selectionManager.selectedObject;

    if (selectedObject?.isConsumer || selectedObject?.isButton || selectedObject?.isButtonChannel) {
      return this.objectIsOnThisPage(selectedObject as ConnectableObject);
    }

    return false;
  }

  objectIsOnThisPage(checkObject: BaseModel): boolean {
    const room = checkObject.parent as Room;
    if (room?.page == this) {
      return true;
    }

    const floor = room?.parent as Floor;
    if (this.isFloorPlan) {
      if (floor?.page == this) {
        return true;
      }
    }

    return false;
  }

  async createRoomInPage(room: Room, p: Point | undefined) {
    if (p) {
      const roomtoFloorDrawingTransform = (room.roomtoFloorDrawingTransform = new RoomToFloorTransform());

      const roomPage = room.getOrCreatePage();
      const placementP = new Point(p.x, p.y);

      const hInMM = roomPage.printableArea.height * 10;

      const d = (hInMM / 8) * mmToPixel;

      const boundingRectInFloorSpace = (room.boundingRectInFloorSpace = new BoundaryRect(
        new Point(placementP.x - d, placementP.y - d),
        new Point(placementP.x + d, placementP.y + d),
      ));

      /////////////////////////////////////////////

      roomtoFloorDrawingTransform.sizeAndPositionFromBoundingRectWithBorder(boundingRectInFloorSpace, roomPage);

      roomPage.backgroundImagePosition.posX = 500;
      roomPage.backgroundImagePosition.posY = 500;
      roomPage.backgroundImagePosition.scale = 1;

      // add memo as roomname
      const roomNameMemo = new Memo({ isRoomTag: true }, roomPage);
      roomPage.memos.add(roomNameMemo);
      roomNameMemo.position = roomtoFloorDrawingTransform.transformPointInvers(p);
      ///
      state.selectionManager.selectedObject = room;
      roomtoFloorDrawingTransform.raiseOnChange();
      room.raiseOnChange();
    }
  }

  addButtonToPage(button: Button, p: Point | undefined) {
    if (p) {
      const center = new Point(
        0, // state.settings.consumerIconSizeInPX() / 2,
        1 / 2,
      );
      const placementP = new Point(p.x - center.x, p.y - center.y);
      button.position = placementP;
    }
    this.addButton(button);
    //showOnboarding("connectButton");
    state.lastCreatedObjectForGuide = button;
  }

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;

    if (this.parent?.className == "Floor") {
      showErrToast(itx("pageDocumentType.pasteInFloorPlansNotSupported", "Paste in FloorPlans not supported yet"));
      return [];
    }

    return (await this.parent?.pasteItem(params)) || [];
  }

  dpmm = 30;

  resizeImageAfterLoad(base64Str: string) {
    return new Promise((resolve) => {
      let img = new Image();
      img.src = base64Str;
      img.onload = () => {
        let canvas = document.createElement("canvas");

        const maxWidth = this.pagesize.width * this.dpmm;
        const maxHeight = this.pagesize.height * this.dpmm;

        let width = img.width;
        let height = img.height;

        if (width < height) {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        } else {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        }
        canvas.width = width;
        canvas.height = height;

        let ctx = canvas.getContext("2d");
        if (ctx) ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg");
        resolve(dataUrl);
      };
    });
  }

  getPageSelectionElement(): BaseModel {
    return this.parent as BaseModel;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    this.connections.forEach((connection) => {
      res.push(connection);
    });
    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.connections.mapAfterCopy();
    }
  }

  pointAlreadyUsed(p: Point): boolean {
    let res = false;

    logger.log(`pointAlreadyUsed  ${p.x} ${p.y} `);

    let allConnectableObjects: Array<ConnectableObject> = this.consumers.asArray();
    allConnectableObjects = allConnectableObjects.concat(this.buttons.asArray());

    if (this) {
      allConnectableObjects.forEach((consumer) => {
        const pos = consumer.position;

        const d = 15 * mmToPixel;
        const rect = new DOMRect(pos.x - d, pos.y - d, 2 * d, 2 * d);

        if (pointInRect(rect, p)) {
          res = true;
        }
      });
    }

    return res;
  }
  pointAlreadyUsedFromRoom(p: Point): boolean {
    let res = false;

    // for rooms
    if (this.isFloorPlan) {
      let floor = this.parent as Floor;
      floor.rooms.forEach((room) => {
        if (room.isVisibleInFloorPlan && room.roomtoFloorDrawingTransform && room.page) {
          const transformPosition = room.roomtoFloorDrawingTransform.position;
          const { width, height } = room.page.contentAreaInPixel;

          if (
            p.x > transformPosition.x - width / 2 &&
            p.x < transformPosition.x + width / 2 &&
            p.y > transformPosition.y - height / 2 &&
            p.y < transformPosition.y + height / 2
          ) {
            res = true;
          }
        }
      });
    }
    return res;
  }

  isInsideOfDrawableArea(pointInPx: Point): boolean {
    const maxPoint = new Point(270 * mmToPixel, 180 * mmToPixel);

    const schriftfeld = new Point(maxPoint.x - 70 * mmToPixel, maxPoint.y - 10 * mmToPixel);

    function insideOfRectangle(upperLeftPoint: Point, lowerRightPoint: Point): boolean {
      if (
        pointInPx.x >= upperLeftPoint.x &&
        pointInPx.x <= lowerRightPoint.x &&
        pointInPx.y >= upperLeftPoint.y &&
        pointInPx.y <= lowerRightPoint.y
      ) {
        return true;
      }
      return false;
    }

    if (insideOfRectangle(new Point(0, 0), maxPoint)) {
      // prüfe Schriftfeld

      if (insideOfRectangle(schriftfeld, maxPoint)) {
        return false;
      }
      return true;
    }

    return false;
  }

  findFreePositionForNewObject(pointInPX?: Point): Point {
    const loops = 50;
    const steps = 12;

    if (!pointInPX) {
      // mitte A4 landscape
      pointInPX = new Point((297 / 2) * mmToPixel, (210 / 2) * mmToPixel);
    }

    const orgPoint = new Point(pointInPX.x, pointInPX.y);
    const point = new Point(pointInPX.x, pointInPX.y);

    var i = 0;

    while (this.pointAlreadyUsed(point) && this.isInsideOfDrawableArea(point) && i < steps * loops) {
      //  const delta = deltas[i++ % deltas.length];
      const a = (Math.PI / (steps / 2)) * i++;

      const delta = new Point(Math.cos(a), Math.sin(a));
      const f = 20 * (Math.floor(i / steps) + 1);

      point.x = orgPoint.x + delta.x * f;
      point.y = orgPoint.y + delta.y * f;
    }

    return point;
  }
  findFreePositionForNewRoom(pointInPX?: Point): Point {
    const loops = 50;
    const steps = 12;

    if (!pointInPX) {
      // mitte A4 landscape
      pointInPX = new Point((297 / 2) * mmToPixel, (210 / 2) * mmToPixel);
    }

    const orgPoint = new Point(pointInPX.x, pointInPX.y);
    const point = new Point(pointInPX.x, pointInPX.y);

    var i = 0;

    while (this.pointAlreadyUsedFromRoom(point) && this.isInsideOfDrawableArea(point) && i < steps * loops) {
      //  const delta = deltas[i++ % deltas.length];
      const a = (Math.PI / (steps / 2)) * i++;

      const delta = new Point(Math.cos(a), Math.sin(a));
      const f = 200 * (Math.floor(i / steps) + 1);

      point.x = orgPoint.x + delta.x * f;
      point.y = orgPoint.y + delta.y * f;
    }

    return point;
  }

  getBeginnPointForConsumerFromChannelType(channelType?: ChannelType): Point {
    let point = new Point(150 * mmToPixel, 100 * mmToPixel);

    const channelTypePointRelation = [
      {
        channelTypes: [ChannelType.Switch, ChannelType.Dimmer, ChannelType.TuneableWhite, ChannelType.Rgb],
        x: 150 * mmToPixel,
        y: 100 * mmToPixel,
      },
      { channelTypes: [ChannelType.Blind, ChannelType.Jalousie], x: 150 * mmToPixel, y: 50 * mmToPixel },
      { channelTypes: [ChannelType.Heating], x: 100 * mmToPixel, y: 50 * mmToPixel },
      { channelTypes: [ChannelType.suply230V, ChannelType.suply400V], x: 100 * mmToPixel, y: 100 * mmToPixel },
    ];
    channelTypePointRelation.forEach((value) => {
      if (channelType && value.channelTypes.includes(channelType)) {
        point = new Point(value.x, value.y);
      }
    });

    return point;
  }

  ///////////////////////////////////////////////////////////////////////

  pdfRotation: number | undefined = undefined;

  // -----------------------------------------------------------------------------
  // pdfPageRefId
  // -----------------------------------------------------------------------------
  _pdfPageRefId?: string;
  get pdfPageRefId(): string | undefined {
    return this._pdfPageRefId;
  }

  set pdfPageRefId(newValue: string | undefined) {
    if (this._pdfPageRefId === newValue) return;
    this.beforeChange("pdfPageRefId");
    this._pdfPageRefId = newValue;
    this.changed("pdfPageRefId");
  }

  // -----------------------------------------------------------------------------

  get pdfPageRef(): Page | undefined {
    return this.state.objectRegistry.getObject(this.pdfPageRefId);
  }

  set pdfPageRef(newValue: Page | undefined) {
    this.pdfPageRefId = newValue?.id;
  }

  // -----------------------------------------------------------------------------
  // pageNumber
  // -----------------------------------------------------------------------------
  _pageNumber: number | undefined = undefined;
  get pageNumber(): number | undefined {
    return this._pageNumber;
  }

  set pageNumber(newValue: number | undefined) {
    if (this._pageNumber === newValue) return;
    this.beforeChange("pageNumber");
    this._pageNumber = newValue;
    this.changed("pageNumber");
  }

  // -----------------------------------------------------------------------------

  get usageParentIds(): Array<string> {
    let usage = new Array<string>();
    state.allFloorPags().forEach((page) => {
      if (
        page.pdfPageRefId == this.id &&
        page.parent &&
        (page.pageNumber == this.pageNumber || (this.pageNumber == undefined && page.pageNumber == 1))
      ) {
        usage.push(page.parent.id);
      }
    });
    return usage;
  }

  _pdfFile: PdfFile | undefined = undefined;

  pdfFileId(): string {
    return `${state.projectMetadata.id}-${this.id}.pdf`;
  }

  savePdfFile() {
    if (this._pdfFile) {
      this._pdfFile.saveToDatabase(this.pdfFileId());
    }
  }

  loadPdfFile() {
    this.pdfFile.loadFromDatabase(this.pdfFileId());
  }

  get pdfFile(): PdfFile {
    if (!this._pdfFile) {
      this._pdfFile = new PdfFile();
    }
    return this._pdfFile;
  }

  resetIsNewOnAllConnections() {
    this.connections.forEach((connection) => {
      if (connection.isNew) {
        connection.isNew = false;
        connection.raiseOnChange("isNew");
      }
    });
  }

  get isBuildingPlan(): boolean {
    return this.parent?.tag == fixedPageGroupTags.buildingPlans;
  }

  removeBackgroundImage() {
    this.backgroundImgUrl = "";
    this.pdfPageRef = undefined;
    this.pageNumber = undefined;
  }

  async setBackgroundImageFromBuildingPdf(refPage: Page, pageNumber: number) {
    this.pdfPageRef = refPage;
    this.pageNumber = pageNumber;
    if (refPage.documentType.type == PageTypes.pdf) {
      // set here the backgroundImgUrl

      //      const dpmm = 6;  // entspricht 150 dpi
      const dpmm = 12; // entspricht 300 dpi

      showGlobalWaitIndicator(true, `Hintergrundbild wird vorbereitet. Bitte warten...`);
      try {
        const w = this.pagesize.width * 10 * dpmm;
        const h = this.pagesize.height * 10 * dpmm;
        const imgUrl = await refPage.pdfFile.convertPdfToDataUrl({
          pageNumber: pageNumber,
          targetWidth: w,
          targetHeight: h,
          rotation: this.pdfRotation || 0,
        });

        // Erst NACH convertPdfToDataUrl abfragen: das aktiviert intern das PDF (pdfFile.activate()).
        // Vorher ist pdfFile.pdf noch undefined, getPageSize() scheitert dann innerlich (abgefangen,
        // liefert undefined) - der Skalierungs-Block unten hat dadurch nie gegriffen und
        // backgroundImagePosition.scale blieb auf dem Default stehen, statt auf den Faktor
        // 300dpi-Renderbild -> Bildschirm-Pixel - daher der massive Zoom.
        const backgroundPdfPageSize = await refPage.pdfFile.getPageSize(pageNumber);

        if (backgroundPdfPageSize) {
          const f = this.contentAreaInPixel.width / w;
          this.backgroundImagePosition.scale = f;
          // this.backgroundImagePosition.width = backgroundPdfPageSize?.width;
          // this.backgroundImagePosition.height = backgroundPdfPageSize?.height;
          this.backgroundImagePosition.width = undefined; // backgroundPdfPageSize?.width;
          this.backgroundImagePosition.height = undefined; // backgroundPdfPageSize?.height;
        }

        /// const result = await this.resizeImageAfterLoad(imgUrl) as string;
        const result = imgUrl;
        this.backgroundImgUrl = result;
        this.raiseOnChange("backgroundImgUrl");
      } catch (ex) {
        logger.error(`Fehler beim Setzen des PDF-Hintergrundbildes: ${(ex as Error)?.message}`, ex);
        showErrToast(
          itx(
            "page.setBackgroundImageFromBuildingPdfFailed",
            "Das Hintergrundbild konnte nicht aus dem PDF erzeugt werden.",
          ),
        );
      } finally {
        showGlobalWaitIndicator(false);
      }
    }
  }

  get isBackgroundImageFromFloorPlan(): boolean {
    if (this?.parent?.className == "Room") {
      return (this?.parent as Room).roomtoFloorDrawingTransform != undefined;
    }

    return false;
  }

  get isRoomOrFloorPlan(): boolean {
    return this.documentType.type == PageTypes.roomplan || this.documentType.type == PageTypes.floorplan;
  }

  getRoomFromPointInBoundingReact(pFloor: Point): Room | undefined {
    if (this.isFloorPlan) {
      const floor = this.parentFloor;
      if (floor) {
        for (const [id, room] of floor.rooms.items) {
          if (!room.isFloorGeneralRoom && pointInBounderyRect(room.boundingRectInFloorSpace, pFloor)) {
            return room;
          }
        }
        return floor.getRoomGeneral();
      }
    }

    return undefined;
  }

  get isFloorPlan(): boolean {
    return this.parent?.className == "Floor";
  }

  get isRoomPlan(): boolean {
    return this.parent?.className == "Room";
  }

  get parentFloor(): Floor | undefined {
    if (this.isFloorPlan) {
      return this.parent as Floor;
    }
    return undefined;
  }

  get parentRoom(): Room | undefined {
    if (this.isRoomPlan) {
      return this.parent as Room;
    } else if (this.isFloorPlan) {
      return (this.parent as Floor)?.getRoomGeneral();
    }

    return undefined;
  }

  freezeFloorPosition() {
    this.getDrawingItems(true).forEach((item) => {
      if (item.className != "Memo" || !(item as Memo).isRoomTag) {
        item.pFloorFrozen = item.positionInFloorSpace;
      }
    });
  }

  unfreezeFloorPosition() {
    this.getDrawingItems(true).forEach((item) => {
      if (item.pFloorFrozen) {
        item.positionInFloorSpace = item.pFloorFrozen;
      }
    });
  }

  setNewScaleForBackgroundImageFromFloorplan(newScale: number) {
    const room = this.parentRoom;
    if (room && room.roomtoFloorDrawingTransform) {
      this.freezeFloorPosition();
      room.roomtoFloorDrawingTransform.setNewScale1(newScale, this);
      this.unfreezeFloorPosition();

      room.raiseOnChange();
    }
  }

  getDrawingItems(includeMemos?: boolean): Array<ConnectableObject> {
    let items = (this.consumers.asArray() as Array<ConnectableObject>).concat(this.buttons.asArray());
    if (includeMemos) {
      items = items.concat(this.memos.asArray());
    }
    return items;
  }

  async setBackgroundImagePosition() {
    let res = this.backgroundImgUrl;

    const room = this.parentRoom;
    if (!res && room && room.roomtoFloorDrawingTransform) {
      //
      // room to floor drawing transform
      //

      const floorPage = (room.parent as Floor)?.page;

      let floorImage: HTMLImageElement | undefined;
      if (floorPage) {
        floorImage = await floorPage?.loadBackgroundImage();
      }

      if (floorImage) {
        let boundingRectInFloorBitmapSpace = room.getBoundingRectInFloorBitmapSpace(floorImage);
        let scale = room.getScaleFloorspaceToBitmapSpace(floorImage);

        if (boundingRectInFloorBitmapSpace) {
          const boundingRectInFloorBitmapSpace_WithPadding = boundingRectInFloorBitmapSpace.addPadding(
            room.paddingForBoundingRectInFloorSpace * scale,
          );

          const r = room.boundingRectInFloorSpaceWithPadding;
          const p1 = room.roomtoFloorDrawingTransform.transformPointInvers(r.min);
          const p2 = room.roomtoFloorDrawingTransform.transformPointInvers(r.max);

          room.roomtoFloorDrawingTransform.scaleAtSubImagesCreationTime = room.roomtoFloorDrawingTransform.scale;
          const w1 = p2.x - p1.x;
          const h1 = p2.y - p1.y;

          this.backgroundImagePosition.posX = p1.x;
          this.backgroundImagePosition.posY = p1.y;

          this.backgroundImagePosition.width = w1;
          this.backgroundImagePosition.height = h1;

          this.backgroundImagePosition.scale = 1;
        }
      }
    }
  }

  _clippedImageUrl?: string;
  _clippedHeight?: number;
  _clippedWidth?: number;

  _clippedImageUrlPdf?: string;
  _clippedHeightPdfIn300Dpi?: number;
  _clippedWidthPdfIn300Dpi?: number;

  _clippedHeightPdf?: number;
  _clippedWidthPdf?: number;

  _clippedImageScale: number = 1;
  _clippedImageLeft: number = 0;
  _clippedImageTop: number = 0;

  async createClippedImage(): Promise<string> {
    let res = this.backgroundImgUrl;

    const room = this.parentRoom;
    if (!res && room && room.roomtoFloorDrawingTransform) {
      await this.setBackgroundImagePosition();

      const floorPage = (room.parent as Floor)?.page;
      let floorImage: HTMLImageElement | undefined;
      if (floorPage) {
        floorImage = await floorPage?.loadBackgroundImage();
      }

      if (floorImage) {
        let boundingRectInFloorBitmapSpace = room.getBoundingRectInFloorBitmapSpace(floorImage);
        let scale = room.getScaleFloorspaceToBitmapSpace(floorImage);

        if (boundingRectInFloorBitmapSpace) {
          const boundingRectInFloorBitmapSpace_WithPadding = boundingRectInFloorBitmapSpace.addPadding(
            room.paddingForBoundingRectInFloorSpace * scale,
          );

          const canvas = document.createElement("canvas");
          canvas.width = boundingRectInFloorBitmapSpace_WithPadding.width;
          canvas.height = boundingRectInFloorBitmapSpace_WithPadding.height;

          const context = canvas.getContext("2d");
          if (context) {
            context.fillStyle = "white";
            context.fillRect(0, 0, canvas.width, canvas.height);

            context.globalAlpha = (100 - (floorPage?.backgroundImagePosition.fade || 0)) / 100;
            context.drawImage(
              floorImage,
              boundingRectInFloorBitmapSpace_WithPadding.min.x,
              boundingRectInFloorBitmapSpace_WithPadding.min.y,
              boundingRectInFloorBitmapSpace_WithPadding.width,
              boundingRectInFloorBitmapSpace_WithPadding.height,
              0,
              0,
              canvas.width,
              canvas.height,
            );
          }

          res = canvas.toDataURL("image/jpeg", 0.98);

          this._clippedImageLeft = this.backgroundImagePosition.posX || 0;
          this._clippedImageTop = this.backgroundImagePosition.posY || 0;

          const cssPixelTo300DPI = 300 / 96;
          this._clippedWidth = canvas.width / cssPixelTo300DPI;
          this._clippedHeight = canvas.height / cssPixelTo300DPI;

          this._clippedWidthPdf = this._clippedWidth;
          this._clippedHeightPdf = this._clippedHeight;

          this._clippedImageUrl = res;
        }
      }
    } else if (this.backgroundImgUrl) {
      const bgImage = await dataUrlToCanvas(this.backgroundImgUrl);

      if (bgImage) {
        let scale = this.backgroundImagePosition.scale;

        const roomCanvas = document.createElement("canvas");
        roomCanvas.width = bgImage.width * scale;
        roomCanvas.height = bgImage.height * scale;

        const context = roomCanvas.getContext("2d");
        if (context) {
          context.fillStyle = "white";
          context.fillRect(0, 0, roomCanvas.width, roomCanvas.height);

          context.drawImage(bgImage, 0, 0, bgImage.width, bgImage.height, 0, 0, roomCanvas.width, roomCanvas.height);
        }

        this._clippedWidth = roomCanvas.width;
        this._clippedHeight = roomCanvas.height;
        this._clippedImageUrl = roomCanvas.toDataURL("image/jpeg", 0.98);
      }
    }

    return res;
  }

  async adjustBackgroundImageForPdf() {
    let left = this.backgroundImagePosition.posX || 0;
    let top = this.backgroundImagePosition.posY || 0;

    let bgImage: HTMLCanvasElement | undefined;
    try {
      if (this.backgroundImgUrl && this.backgroundImgUrl != "") {
        bgImage = await dataUrlToCanvas(this.backgroundImgUrl);
      } else if (this._clippedImageUrl && this._clippedImageUrl != "") {
        bgImage = await dataUrlToCanvas(this._clippedImageUrl);

        if (bgImage) {
          this._clippedWidthPdf = this.backgroundImagePosition.width || 0;
          this._clippedHeightPdf = this.backgroundImagePosition.height || 0;

          this._clippedImageUrlPdf = this._clippedImageUrl;

          //          this._clippedImageScale = 0;
          //          this._clippedImageLeft = 0;
          //          this._clippedImageTop = 0;

          return;
        }
      }

      if (bgImage) {
        //
        // hier müssen wir die "äussere Position" des Images
        // in innerer Pixel des Images umrechnen
        // dazu müssen wir die Auflösung des Image in ImagePixel / DocumentPixel kennen.
        //

        // CSS-Pixel basieren auf 96 DPI (Standard-Bildschirmauflösung)

        // Pixel bei 300 DPI = CSS-Pixel × (300 DPI ÷ 96 DPI)
        // Pixel bei 300 DPI = CSS-Pixel × 3.125

        const cssPixelTo300DPI = 300 / 96;

        const fixLeft = left * cssPixelTo300DPI;
        const fixTop = top * cssPixelTo300DPI;

        const wInCm = this.printableArea.width - 2 * this.frameWidth;
        const hInCm = this.printableArea.height - 2 * this.frameWidth;

        const wIn300DPI = wInCm * 10 * mmToPixel * cssPixelTo300DPI;
        const hIn300Dpi = hInCm * 10 * mmToPixel * cssPixelTo300DPI;

        const pdfPageCanvas = document.createElement("canvas");
        pdfPageCanvas.width = wIn300DPI;
        pdfPageCanvas.height = hIn300Dpi;

        const scale = this.backgroundImagePosition.scale;

        const context = pdfPageCanvas.getContext("2d");
        if (context) {
          context.fillStyle = "#fff";
          context.fillRect(0, 0, pdfPageCanvas.width, pdfPageCanvas.height);

          context.globalAlpha = (100 - this.backgroundImagePosition.fade) / 100;
          context.drawImage(
            bgImage,
            0,
            0,
            bgImage.width,
            bgImage.height,
            fixLeft,
            fixTop,
            bgImage.width * scale * cssPixelTo300DPI,
            bgImage.height * scale * cssPixelTo300DPI,
          );

        }

        this._clippedWidthPdf = pdfPageCanvas.width / cssPixelTo300DPI;
        this._clippedHeightPdf = pdfPageCanvas.height / cssPixelTo300DPI;

        this._clippedWidthPdfIn300Dpi = pdfPageCanvas.width;
        this._clippedHeightPdfIn300Dpi = pdfPageCanvas.height;

        this._clippedImageUrlPdf = pdfPageCanvas.toDataURL("image/jpeg", 0.98);

        this._clippedImageScale = 0;
        this._clippedImageLeft = 0;
        this._clippedImageTop = 0;
      }
    } catch (error) {
      logger.error("Fehler beim Anpassen des Hintergrundbildes für PDF: " + error);
    }
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);

    this.connections.consistencyCheck(this);
    this.memos.consistencyCheck(this);
  }
}
